import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";

import { orgIdSelector } from "../../../../selectors/orgIdSelector";
import { ErrorBanner } from "../../../Components/ErrorBanner";
import ActionsBar from "./ActionsBar";
import { getErrorMsgElt } from "./helpers";
import ConnectedGraph from "./ConnectedGraph";
import DatepickerContainer from "./DatepickerContainer";
import RefreshButton from "./RefreshButton";
import ResourceTitle from "./ResourceTitleConnected";
import Talkers from "../Talkers";
import Threats from "../Threats";
import WindowsEvts from "../WindowsEvts";

import "./Detail.scss";

/*
Component
*/

const Detail = (props) => {
  const {
    dispatchClearErr,
    dispatchRedirectToIndex,
    errorMessages,
    fetchTalkers,
    fetchThreats,
    fetchWindowsEvts,
    fetchGraph,
    graphDatesSelector,
    graphEntriesSelector,
    graphParamsSelector,
    hasTalkers,
    hasThreats,
    hasWindowsEvts,
    idSelector,
    intervalSelector,
    orgId,
    receiveDateRange,
    talkersSelector,
    threatsSelector,
    titleSelector,
    windowsEvtsSelector,
  } = props;

  const firstRun = useRef(true);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }

    dispatchRedirectToIndex(orgId);
  }, [dispatchRedirectToIndex, orgId]);

  const onClickRefresh = () => {
    setRefresh(!refresh);
  };

  return (
    <div className="resources-view resources-detail">
      <ErrorBanner
        message={getErrorMsgElt(errorMessages)}
        dismiss={dispatchClearErr}
      />

      <ActionsBar left={1}>
        <ResourceTitle titlePrefix="" titleSelector={titleSelector} />

        <DatepickerContainer
          intervalSelector={intervalSelector}
          itemIdSelector={idSelector}
          receiveDateRange={receiveDateRange}
        />

        <RefreshButton onClick={onClickRefresh} />
      </ActionsBar>

      <div className="detail-section">
        <div className="detail-section-body">
          <ConnectedGraph
            fetchGraph={fetchGraph}
            graphDatesSelector={graphDatesSelector}
            graphEntriesSelector={graphEntriesSelector}
            graphParamsSelector={graphParamsSelector}
            graphTitle="Log Events"
            idSelector={idSelector}
            intervalSelector={intervalSelector}
            refresh={refresh}
          />
        </div>
      </div>

      <div className="two-column-div">
        <div className="detail-section">
          {hasTalkers && (
            <div className="detail-section-header">Top 10 Talkers</div>
          )}

          <div className="detail-section-body">
            <Talkers
              dataSelector={talkersSelector}
              fetchData={fetchTalkers}
              idSelector={idSelector}
              intervalSelector={intervalSelector}
              refresh={refresh}
            />
          </div>
        </div>

        <div className="detail-section">
          {hasThreats && (
            <div className="detail-section-header">Top 10 Threats</div>
          )}

          <div className="detail-section-body">
            <Threats
              dataSelector={threatsSelector}
              fetchData={fetchThreats}
              idSelector={idSelector}
              intervalSelector={intervalSelector}
              refresh={refresh}
            />
          </div>
        </div>

        <div className="detail-section">
          {hasWindowsEvts && (
            <div className="detail-section-header">Top 10 Windows Events</div>
          )}

          <div className="detail-section-body">
            <WindowsEvts
              dataSelector={windowsEvtsSelector}
              fetchData={fetchWindowsEvts}
              idSelector={idSelector}
              intervalSelector={intervalSelector}
              refresh={refresh}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

Detail.propTypes = {
  dispatchClearErr: PropTypes.func.isRequired,
  dispatchRedirectToIndex: PropTypes.func.isRequired,
  errorMessages: PropTypes.arrayOf(PropTypes.string).isRequired,
  fetchGraph: PropTypes.func.isRequired,
  fetchTalkers: PropTypes.func.isRequired,
  fetchThreats: PropTypes.func.isRequired,
  fetchWindowsEvts: PropTypes.func.isRequired,
  graphDatesSelector: PropTypes.func.isRequired,
  graphEntriesSelector: PropTypes.func.isRequired,
  graphParamsSelector: PropTypes.func.isRequired,
  hasTalkers: PropTypes.bool.isRequired,
  hasThreats: PropTypes.bool.isRequired,
  hasWindowsEvts: PropTypes.bool.isRequired,
  idSelector: PropTypes.func.isRequired,
  intervalSelector: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  receiveDateRange: PropTypes.func.isRequired,
  talkersSelector: PropTypes.func.isRequired,
  threatsSelector: PropTypes.func.isRequired,
  titleSelector: PropTypes.func.isRequired,
  windowsEvtsSelector: PropTypes.func.isRequired,
};

Detail.defaultProps = {};

const makeMapStateToProps = (
  _,
  {
    detailErrorMsgSelector = () => [],
    graphDatesSelector,
    talkersSelector,
    threatsSelector,
    windowsEvtsSelector,
  }
) => {
  const mapStateToProps = (state) => {
    const errorMessages = detailErrorMsgSelector(state);

    const orgId = orgIdSelector(state);

    const { windowEnd = "", windowStart = "" } = graphDatesSelector(state);

    const hasTalkers = !!talkersSelector(state).length;
    const hasThreats = !!threatsSelector(state).length;
    const hasWindowsEvts = !!windowsEvtsSelector(state).length;

    return {
      errorMessages,
      hasTalkers,
      hasThreats,
      hasWindowsEvts,
      orgId,
      windowEnd,
      windowStart,
    };
  };

  return mapStateToProps;
};

const makeMapDispatchToProps = (_, { clearDetailErrors, redirectToIndex }) => {
  const mapDispatchToProps = (dispatch) => ({
    dispatchClearErr: () => {
      dispatch(clearDetailErrors);
    },

    dispatchRedirectToIndex: (orgId) => dispatch(redirectToIndex(orgId)),
  });

  return mapDispatchToProps;
};

// export default connect(mapStateToProps, mapDispatchToProps)(Detail);
// Default export
const DetailConnected = connect(
  makeMapStateToProps,
  makeMapDispatchToProps
)(Detail);

export default DetailConnected;
